import {
    Form as FormikForm,
    Formik,
    FormikValues,
    Field as FormikField,
    FormikErrors,
    FormikTouched,
    useFormikContext,
    FieldProps as FormikFieldProps,
    FormikHelpers,
    FormikProps,
} from "formik";
import React, { KeyboardEventHandler, ReactNode, Ref } from "react";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { clsx } from "clsx";
import { FormError, FormRow } from "@/common/FormLayout";
import { Input } from "@/catalyst/input.tsx";

const Form = (props: {
    initialValues?: FormikValues;
    validate?: (values: FormikValues) => Record<string, string>;
    onSubmit: (
        values: FormikValues,
        actions: { setSubmitting: (isSubmitting: boolean) => void },
    ) => void;
    children: (renderProps: {
        errors: FormikErrors<FormikValues>;
        touched: FormikTouched<FormikValues>;
        values: FormikValues;
        setFieldValue: FormikHelpers<FormikValues>["setFieldValue"];
        submitForm: () => Promise<void>;
    }) => ReactNode;
    validationSchema?: unknown;
    formikRef?: Ref<FormikProps<FormikValues>>;
}): ReactNode => {
    return (
        <Formik
            initialValues={props.initialValues || {}}
            validate={props.validate}
            onSubmit={props.onSubmit}
            validationSchema={props.validationSchema}
            innerRef={props.formikRef}
        >
            {({ errors, touched, submitForm, values, setFieldValue }) => (
                <FormikForm>
                    {props.children({
                        errors,
                        touched,
                        submitForm,
                        values,
                        setFieldValue,
                    })}
                </FormikForm>
            )}
        </Formik>
    );
};

export type FieldProps = {
    name: string;
    label?: string | ReactNode;
    labelSelect?: boolean;
    wrapperClassName?: string;
    type?: "email" | "text" | "hidden";
    placeholder?: string;
    description?: ReactNode;
    as?: "textarea" | React.ComponentType<FormikFieldProps["field"]>;
    error?: string;
    touched?: boolean;
    autoFocus?: boolean;
    autoComplete?: "off";
    disabled?: boolean;
    id?: string;
    afterFieldItem?: ReactNode;
};

const Field = ({
    id,
    name,
    as = Input,
    label,
    placeholder,
    description,
    disabled,
    type = "text",
    error,
    touched,
    autoFocus = false,
    autoComplete,
    wrapperClassName,
    afterFieldItem,
    labelSelect,
}: FieldProps): ReactNode => {
    const { submitForm } = useFormikContext();
    const onKeyDown: KeyboardEventHandler = (event) => {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            submitForm();
        }
    };
    const isTextArea = as === "textarea";

    return (
        // The house row, stacked: a dialog is too narrow for the label beside
        // the field, but the order is the same, name and explanation first.
        // No margin of its own: the `FormStack` around the fields sets the
        // distance between them.
        <div
            key={name}
            className={clsx(
                {
                    hidden: type === "hidden",
                },
                wrapperClassName,
            )}
        >
            <FormRow
                stacked
                label={
                    // A label somebody has to copy from, such as the word a
                    // confirmation asks them to type.
                    labelSelect ? (
                        <span className={"select-text"}>{label}</span>
                    ) : (
                        label
                    )
                }
                description={description}
                aside={afterFieldItem}
            >
                <div className="relative">
                    <FormikField
                        key={name}
                        name={name}
                        id={id || name}
                        as={as}
                        type={type}
                        disabled={disabled}
                        data-1p-ignore
                        autoFocus={autoFocus}
                        onKeyDown={onKeyDown}
                        autoComplete={autoComplete}
                        className={clsx({
                            ["border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500"]:
                                touched && !!error,
                            ["focus:border-pink-500 focus:ring-pink-500 "]:
                                !touched || !error,
                            "min-h-52": isTextArea,
                        })}
                        placeholder={placeholder}
                    />
                    {!!error && touched && (
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <ExclamationCircleIcon
                                className={"h-5 w-5 text-red-500"}
                            />
                        </div>
                    )}
                </div>
                <FormError>{touched && error}</FormError>
            </FormRow>
        </div>
    );
};

Form.Field = Field;

export default Form;
